import { useMutation } from '@apollo/client'
import { Table, TableBody, TableCell, TableRow } from '@mui/material'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { FloraTypography, Flex, FloraButton } from '@grupoboticario/flora-react'
import { EyeIcon, PencilSimpleIcon } from '@grupoboticario/flora-react-icons'

import { ModalFinishCampaign } from '@/components/ModalFinishCampaign'
import BackofficeTableContainer from '@/components/Table/Generic/BackofficeTableContainer/BackofficeTableContainer'
import TableHeader from '@/components/Table/Generic/TableHeader/TableHeader'
import TablePaginationDefault from '@/components/Table/Generic/TablePaginationDefault/TablePaginationDefault'
import {
  STATUS_ABLE_TO_FINISH_CAMPAIGN,
  CAMPAIGN_STATUS,
  STATUS_LABEL,
  STATUS_ABLE_TO_EDIT_CAMPAIGN
} from '@/constants/accelerationTable'
import { useUserContext } from '@/context/user'
import UPDATE_STATUS_CAMPAIGN from '@/graphql/mutation/updateStatusCampaign'
import { BackofficeTableStyle } from '@/utils/customStyle/tableStyle'
import { Actions } from '@/utils/dictionaries/actionsDictionary/actions'
import { formatDateWithLocale } from '@/utils/format/formatDateWithLocale'
import { columns } from '@/utils/tableColumns/accelerationTable'
import triggerToastError from '@/utils/triggerToastError/triggerToastError'
import triggerToastSuccess from '@/utils/triggerToastSuccess/triggerToastSuccess'

import * as S from './AccelerationTable.styles'
import { colorLabel, statusLabelVariants, NO_TOKEN_COLORS } from './styles'

const AccelerationTable = ({ data, loading, refetch, onPaginationChange = () => {} }) => {
  const navigate = useNavigate()
  const { user: loggedUser, hasPermission } = useUserContext()
  const [campaignIDToFinish, setCampaignIDToFinish] = useState(null)

  const [openFinishModal, setOpenFinishModal] = useState(false)
  const [updateStatusCampaignCall, { loading: updateStatusCampaignLoading }] = useMutation(UPDATE_STATUS_CAMPAIGN)

  const getEmailPrefix = (email) => {
    if (!email) return ''
    return email.split('@')[0]
  }

  const statusAbleToFinishCampaign = (status) =>
    STATUS_ABLE_TO_FINISH_CAMPAIGN.includes(status) && hasPermission(Actions.CREATE_ACCELERATION)

  const statusAbleToEditCampaign = (status: string) =>
    STATUS_ABLE_TO_EDIT_CAMPAIGN.includes(status) && hasPermission(Actions.EDIT_ACCELERATION)

  const handleSubmit = async (e, values) => {
    e.preventDefault()

    try {
      await updateStatusCampaignCall({
        variables: {
          campaignId: campaignIDToFinish,
          statusChange: {
            status: CAMPAIGN_STATUS.CANCELLED,
            justification: values.description,
            responsible: loggedUser?.email
          }
        }
      })

      triggerToastSuccess(false, 'Campanha de acelerador encerrada com sucesso!', () => {
        setOpenFinishModal(false)

        if (refetch) refetch()
      })
    } catch {
      triggerToastError('Ops! Ocorreu um erro ao encerrar a campanha de acelerador. Tente novamente.')
    }
  }

  const handleFinishCampaign = (status) => {
    if (!statusAbleToFinishCampaign(status)) return
    setOpenFinishModal(true)
  }

  return (
    <>
      <BackofficeTableStyle />
      <BackofficeTableContainer columns={columns} loading={loading}>
        {data?.items.length === 0 && !loading && (
          <Flex align="center" css={S.emptyStateCss} justify="center">
            <FloraTypography css={S.emptyStateTextCss} fontSize="bodyLargeStandard">
              Nenhuma campanha de acelerador encontrada.
            </FloraTypography>
          </Flex>
        )}

        {data?.items.length > 0 && (
          <Table aria-label="Tabela das campanhas de aceleradores">
            <TableHeader columns={columns} isSortable={false} />
            <TableBody>
              {data?.items?.map((row) => {
                return (
                  <TableRow key={row?.campaignId}>
                    <TableCell>
                      <FloraTypography fontSize="bodySmallStandard">{row?.name}</FloraTypography>
                    </TableCell>
                    <TableCell>
                      <FloraTypography fontSize="bodySmallStandard">{row?.campaignId}</FloraTypography>
                    </TableCell>
                    <TableCell>
                      <Flex direction="column" gap="$1">
                        <Flex gap="$1">
                          <FloraTypography fontSize="bodySmallStandard">
                            {formatDateWithLocale(row?.startDate)}
                          </FloraTypography>
                          -
                        </Flex>
                        <FloraTypography fontSize="bodySmallStandard">
                          {formatDateWithLocale(row?.endDate)}
                        </FloraTypography>
                      </Flex>
                    </TableCell>
                    <TableCell>
                      <Flex align="center" gap="$2">
                        <Flex css={statusLabelVariants[row?.status]} style={colorLabel} />
                        <FloraTypography fontSize="bodySmallStandard">{STATUS_LABEL[row?.status]}</FloraTypography>
                      </Flex>
                    </TableCell>
                    <TableCell>
                      <FloraTypography fontSize="bodySmallStandard">{getEmailPrefix(row?.responsible)}</FloraTypography>
                    </TableCell>
                    <TableCell>
                      <Flex align="center" gap="$4">
                        <FloraButton
                          aria-label="Ver detalhes"
                          data-testid={`acceleration-view-${row?.campaignId}`}
                          has="iconOnly"
                          hierarchy="tertiary"
                          size="small"
                          onClick={() => navigate(`/acceleration/${row?.campaignId}`)}
                          icon={<EyeIcon color={NO_TOKEN_COLORS.BLUE} size={24} />}
                        />

                        <FloraButton
                          aria-label="Editar"
                          data-testid={`acceleration-edit-${row?.campaignId}`}
                          has="iconOnly"
                          hierarchy="tertiary"
                          disabled={!statusAbleToEditCampaign(row?.status)}
                          size="small"
                          onClick={() => navigate(`/acceleration/${row?.campaignId}/edit`)}
                          icon={
                            <PencilSimpleIcon
                              css={S.getEditActionCss(statusAbleToEditCampaign(row?.status))}
                              size={24}
                            />
                          }
                        />

                        <FloraTypography
                          as="a"
                          css={S.getFinishActionCss(statusAbleToFinishCampaign(row?.status))}
                          fontSize="bodySmallStandard"
                          onClick={() => {
                            handleFinishCampaign(row?.status)
                            setCampaignIDToFinish(row?.campaignId)
                          }}
                        >
                          Encerrar
                        </FloraTypography>
                      </Flex>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </BackofficeTableContainer>
      <div style={{ display: data?.items.length > 0 ? undefined : 'none' }}>
        <TablePaginationDefault nextPageToken={data?.nextPage || ''} onPaginationChange={onPaginationChange} />
      </div>
      <ModalFinishCampaign
        handleSubmit={handleSubmit}
        isLoading={updateStatusCampaignLoading}
        openFinishModal={openFinishModal}
        setOpenFinishModal={setOpenFinishModal}
      />
    </>
  )
}

export default AccelerationTable
